import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ServerUnreachableScreen } from "@/components/ServerStatus";
import { ShieldX } from "lucide-react";

export function ProtectedRoute({ children, role, permission }) {
  const { user, has, unreachable, sessionUnverified } = useAuth();

  if (user === null) {
    // The session is UNKNOWN, not over: if the server can't be reached, say that instead of
    // showing a login form (which would claim the session ended) or a stuck spinner.
    if (unreachable) return <ServerUnreachableScreen />;
    // R-10: the server answered, but with a 500, so it never said whether the session is good.
    // "Logged out" was the bug; "can't reach the server" would be a different lie.
    if (sessionUnverified) return <ServerUnreachableScreen variant="unverified" />;
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-xs text-slate-500" data-testid="auth-loading">
        AUTHENTICATING…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;

  const allowed =
    user.role === "Admin" ||
    (!role && !permission) ||
    (role && user.role === role) ||
    (permission && has(permission));

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="forbidden-page">
        <div className="border border-slate-200 bg-white p-8 max-w-md text-center" data-testid="yard-production-denied">
          <ShieldX className="w-10 h-10 mx-auto text-destructive" strokeWidth={1.5} />
          <h2 className="font-display text-2xl font-bold mt-4">403 — FORBIDDEN</h2>
          <p className="text-sm text-slate-600 mt-2">
            Your role <span className="font-mono font-bold">{user.role_name || user.role}</span> does not have access to this page.
            Access is denied by the server on every API call, not just hidden here.
          </p>
        </div>
      </div>
    );
  }
  return children;
}
